import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Timer, Clock, Sunrise, CalendarDays, ChevronUp, ChevronDown } from 'lucide-react'

const DAYS = ['Pa', 'Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct']
const MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']

const pad = (n) => String(n).padStart(2, '0')
const fmtTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
const fmtLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

/** Türkçe doğal dil → tarih. "yarın 14:00", "2 saat sonra", "30 dk sonra", "pazartesi 9",
 *  "haftaya", "bugün 17:30", "3 gün sonra", "14:00" */
export function parseNatural(text) {
  const t = (text || '').toLocaleLowerCase('tr').trim()
  if (!t) return null
  const now = new Date()
  const d = new Date(now)
  d.setSeconds(0, 0)

  const rel = t.match(/(\d+)\s*(dakika|dk|saat|sa|gün|gun|hafta)\s*(sonra)?/)
  if (rel) {
    const n = parseInt(rel[1], 10)
    const u = rel[2]
    if (u.startsWith('d')) d.setMinutes(d.getMinutes() + n)
    else if (u.startsWith('s')) d.setHours(d.getHours() + n)
    else if (u.startsWith('g') || u.startsWith('gu')) d.setDate(d.getDate() + n)
    else d.setDate(d.getDate() + n * 7)
    return d
  }

  const time = t.match(/(\d{1,2})[:.](\d{2})/) || t.match(/saat\s*(\d{1,2})\b/)
  const hh = time ? parseInt(time[1], 10) : null
  const mm = time && time[2] ? parseInt(time[2], 10) : 0

  const WEEK = ['pazar', 'pazartesi', 'salı', 'çarşamba', 'perşembe', 'cuma', 'cumartesi']
  const wi = WEEK.findIndex((w) => t.includes(w))

  if (t.includes('yarın')) d.setDate(d.getDate() + 1)
  else if (t.includes('öbür gün') || t.includes('obur gun')) d.setDate(d.getDate() + 2)
  else if (t.includes('haftaya') || t.includes('gelecek hafta')) {
    d.setDate(d.getDate() + ((8 - d.getDay()) % 7 || 7))
    if (hh === null) { d.setHours(9, 0) ; return d }
  } else if (wi >= 0) {
    const diff = (wi - d.getDay() + 7) % 7 || 7
    d.setDate(d.getDate() + diff)
  } else if (!t.includes('bugün') && hh === null) {
    return null
  }

  if (hh !== null) d.setHours(hh, mm)
  else d.setHours(9, 0)
  if (d <= now) d.setDate(d.getDate() + (hh !== null && !t.includes('yarın') && wi < 0 ? 1 : 0))
  return d
}

/** ClickUp tarzı zamanlama seçici: doğal dil + hızlı seçenekler + takvim + saat */
export default function SchedulePicker({ x, y, onPick, onClose }) {
  const ref = useRef(null)
  const [q, setQ] = useState('')
  const [pos, setPos] = useState({ left: x, top: y })
  const now = new Date()
  const [cursor, setCursor] = useState(new Date(now.getFullYear(), now.getMonth(), 1))
  const [picked, setPicked] = useState(null)          // Date (gün seçilince saat sorulur)
  const [time, setTime] = useState('09:00')

  useLayoutEffect(() => {
    const el = ref.current; if (!el) return
    const r = el.getBoundingClientRect()
    let left = x, top = y
    if (left + r.width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - r.width - 8)
    if (top + r.height > window.innerHeight - 8) top = Math.max(8, y - r.height - 12)
    setPos({ left, top })
  }, [x, y, picked])

  useEffect(() => {
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.() }
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose?.() } }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey, true)
    return () => { document.removeEventListener('mousedown', onDown, true); document.removeEventListener('keydown', onKey, true) }
  }, [onClose])

  const quick = useMemo(() => {
    const mk = (mins) => { const d = new Date(); d.setSeconds(0, 0); d.setMinutes(d.getMinutes() + mins); return d }
    const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1); tomorrow.setHours(8, 0, 0, 0)
    const nextWeek = new Date(); nextWeek.setDate(nextWeek.getDate() + ((8 - nextWeek.getDay()) % 7 || 7)); nextWeek.setHours(8, 0, 0, 0)
    return [
      { icon: Timer, label: '20 dakika sonra', date: mk(20) },
      { icon: Clock, label: '2 saat sonra', date: mk(120) },
      { icon: Sunrise, label: 'Yarın', date: tomorrow },
      { icon: CalendarDays, label: 'Gelecek hafta', date: nextWeek },
    ]
  }, [])

  const natural = useMemo(() => parseNatural(q), [q])

  const grid = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1)
    const start = new Date(first)
    start.setDate(first.getDate() - first.getDay())
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d })
  }, [cursor])

  const commit = (d) => { onPick?.(fmtLocal(d)); onClose?.() }
  const confirmPicked = () => {
    const [h, m] = time.split(':').map((v) => parseInt(v, 10))
    const d = new Date(picked)
    d.setHours(h || 0, m || 0, 0, 0)
    commit(d)
  }

  return createPortal(
    <div ref={ref} style={{ left: pos.left, top: pos.top }}
      className="fixed z-[94] w-[320px] bg-white border border-gray-200 rounded-2xl shadow-2xl overflow-hidden">
      {/* Doğal dil */}
      <div className="p-2.5 border-b border-gray-100">
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && natural) commit(natural) }}
          placeholder={'"Yarın 14:00" yazmayı dene…'}
          className="w-full px-3 py-2 text-sm border-2 border-gray-800 rounded-lg outline-none" />
        {q.trim() && (
          <button disabled={!natural} onClick={() => natural && commit(natural)}
            className={`w-full mt-1.5 px-3 py-1.5 rounded-lg text-[12.5px] text-left ${natural ? 'bg-violet-50 text-violet-700 hover:bg-violet-100' : 'text-gray-400'}`}>
            {natural
              ? `${natural.getDate()} ${MONTHS[natural.getMonth()]} ${natural.getFullYear()} · ${fmtTime(natural)} — Enter ile zamanla`
              : 'Anlaşılmadı — takvimden seç'}
          </button>
        )}
      </div>

      {/* Hızlı seçenekler */}
      {!picked && (
        <div className="py-1 border-b border-gray-100">
          {quick.map((qk) => (
            <button key={qk.label} onClick={() => commit(qk.date)}
              className="w-full flex items-center gap-2.5 px-3 py-1.5 hover:bg-gray-50 text-left">
              <qk.icon size={15} className="text-gray-400 shrink-0" />
              <span className="text-[13px] text-gray-800 flex-1">{qk.label}</span>
              <span className="text-[12px] text-gray-400">
                {sameDay(qk.date, now) ? fmtTime(qk.date) : `${DAYS[qk.date.getDay()]}, ${fmtTime(qk.date)}`}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Takvim */}
      <div className="p-2.5">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[13px] font-semibold text-gray-900 flex-1">
            {MONTHS[cursor.getMonth()]} {cursor.getFullYear()}
          </span>
          <button onClick={() => { setCursor(new Date(now.getFullYear(), now.getMonth(), 1)); setPicked(null) }}
            className="text-[12px] text-gray-500 hover:text-gray-800">Bugün</button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}
            className="p-0.5 text-gray-400 hover:text-gray-700"><ChevronUp size={15} /></button>
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}
            className="p-0.5 text-gray-400 hover:text-gray-700"><ChevronDown size={15} /></button>
        </div>
        <div className="grid grid-cols-7 gap-0.5 mb-0.5">
          {DAYS.map((d) => <div key={d} className="text-[11px] text-gray-400 text-center py-0.5">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {grid.map((d, i) => {
            const other = d.getMonth() !== cursor.getMonth()
            const today = sameDay(d, now)
            const sel = picked && sameDay(d, picked)
            const past = d < new Date(now.getFullYear(), now.getMonth(), now.getDate())
            return (
              <button key={i} disabled={past}
                onClick={() => { setPicked(d); setTime(fmtTime(new Date(now.getTime() + 60 * 60 * 1000))) }}
                className={`h-8 rounded-full text-[12.5px] flex items-center justify-center
                  ${past ? 'text-gray-200 cursor-default' : other ? 'text-gray-300 hover:bg-gray-100' : 'text-gray-800 hover:bg-gray-100'}
                  ${today && !sel ? 'bg-red-500 text-white hover:bg-red-600 font-semibold' : ''}
                  ${sel ? 'bg-violet-600 text-white hover:bg-violet-700 font-semibold' : ''}`}>
                {d.getDate()}
              </button>
            )
          })}
        </div>
      </div>

      {/* Saat onayı */}
      {picked && (
        <div className="flex items-center gap-2 px-2.5 py-2 border-t border-gray-100 bg-gray-50/60">
          <span className="text-[12px] text-gray-500">
            {picked.getDate()} {MONTHS[picked.getMonth()]}
          </span>
          <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
            className="px-2 py-1 text-[12.5px] border border-gray-200 rounded-lg outline-none focus:ring-1 focus:ring-violet-200" />
          <button onClick={() => setPicked(null)} className="ml-auto text-[12px] text-gray-500 hover:text-gray-800">Vazgeç</button>
          <button onClick={confirmPicked}
            className="px-3 py-1 text-[12.5px] font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg">Zamanla</button>
        </div>
      )}
    </div>,
    document.body,
  )
}
